import React, { useState } from 'react';
import useMeasure from 'react-use-measure';
import { useSpring } from 'react-spring';
import { ApIcon, colors } from '@alixpartners/ui-components';

import {
  UPLOADED,
  MAPPING,
  STAGED,
  TFileStatus,
  IUploadedFile,
  IUserFilesBySession,
  DOWNLOADING,
  DELETED,
} from '../../types';
import { UploadedFilesListItem } from './UploadedFilesListItem';
import {
  Container,
  List,
  Heading,
  Wrapper,
  SessionWrapper,
  OtherFilesContainer,
  OtherFilesHeading,
  ButtonIcon,
  OtherFilesList,
  MainWrapper,
  DeleteWrapper,
  ExpiresMessage,
  GroupIconBox,
  IconWrapper,
  GroupHeaderRow,
  GroupName,
  FileRows,
  FileRow,
} from './UploadedFilesList.styles';
import { downloadFile } from '../../utils/downloadFile';
import { timeLeft } from '../../utils/common';
import iconDocument from '../../assets/images/icon-document.svg';

interface IUploadedFilesList {
  files: IUserFilesBySession;
  malformedRowsUrlMap?: { [sessionId: string]: string };
  sessionExpirationDatesMap?: { [sessionId: string]: string };
  status: TFileStatus;
  onOpenMapper?: (sessionId: string, status: TFileStatus) => void;
  onDeleteMappingSession?: (sessionId: string) => any;
  onDeleteFile?: (fileId: string) => any;
  selectedSection?: string;
}

interface IOtherFilesList {
  files: IUploadedFile[];
  onDeleteFile?: (fileId: string) => any;
}

export const UploadedFilesList: React.FC<IUploadedFilesList> = ({
  files,
  malformedRowsUrlMap,
  sessionExpirationDatesMap,
  status,
  onDeleteMappingSession,
  onDeleteFile,
}) => {
  if (Object.keys(files).length === 0) return null;
  let malformedCount = 1;

  return (
    <Container status={status}>
      {status === MAPPING || status === UPLOADED || status === DELETED ? (
        <>
          {Object.keys(files).map((sessionId, index) => {
            const isDownloading = files[sessionId][0].status === DOWNLOADING;
            const filesCount = files[sessionId].length;
            const isSingle = filesCount === 1;
            const timeLeftText = sessionExpirationDatesMap?.[sessionId]
              ? timeLeft(sessionExpirationDatesMap?.[sessionId])
              : '';

            const headerLabel = isDownloading
              ? `Processing (${filesCount} files)`
              : status === DELETED
              ? `Expired mapping session (${filesCount} ${
                  isSingle ? 'file' : 'files'
                })`
              : isSingle
              ? files[sessionId][0].name
              : `File group ${index + 1}`;

            return (
              <SessionWrapper key={sessionId}>
                <GroupHeaderRow>
                  <Wrapper>
                    <GroupIconBox>
                      <img src={iconDocument} alt="" width={14} height={17} />
                    </GroupIconBox>
                    <GroupName>{headerLabel}</GroupName>
                    {timeLeftText && (
                      <ExpiresMessage>{`Expires in ${timeLeftText}`}</ExpiresMessage>
                    )}
                  </Wrapper>
                  {onDeleteMappingSession && (
                    <DeleteWrapper
                      onClick={() => onDeleteMappingSession(sessionId)}
                    >
                      <ApIcon
                        iconName="delete"
                        iconSize={20}
                        iconColor="#4a4a4a"
                      />
                      {!isSingle && 'Delete group'}
                    </DeleteWrapper>
                  )}
                </GroupHeaderRow>
                {!isSingle && (
                  <FileRows>
                    {files[sessionId].map((f) => (
                      <FileRow key={f.id}>{f.name}</FileRow>
                    ))}
                  </FileRows>
                )}
              </SessionWrapper>
            );
          })}
        </>
      ) : (
        <>
          <Heading status={status}>
            <Wrapper>
              <IconWrapper>
                <ApIcon iconColor={colors.grey500} iconName="done_all" />
              </IconWrapper>
              {`Mapped to schema and copied to staging table (${
                Object.keys(files).length
              }) files`}
            </Wrapper>
          </Heading>
          <List>
            {Object.keys(files).map((sessionId, index) => {
              const list = files[sessionId].map((f) => {
                return (
                  <UploadedFilesListItem
                    key={f.id}
                    {...f}
                    onDeleteClick={
                      f.canDelete
                        ? () => onDeleteFile?.(f.id.toString())
                        : undefined
                    }
                  />
                );
              });

              if (files[sessionId][0] && malformedRowsUrlMap?.[sessionId]) {
                return (
                  <React.Fragment key={sessionId}>
                    <UploadedFilesListItem
                      {...files[sessionId][0]}
                      name="Malformed rows"
                      malformedCount={malformedCount++}
                      onDownloadMalformedClick={() =>
                        downloadFile(malformedRowsUrlMap?.[sessionId])
                      }
                      malformed
                    />
                    {list}
                  </React.Fragment>
                );
              }

              return list;
            })}
          </List>
        </>
      )}
    </Container>
  );
};

type TFilesList = Omit<IUploadedFilesList, 'status'>;
export const UploadedList: React.FC<TFilesList> = (props) => (
  <UploadedFilesList status={UPLOADED} {...props} />
);

export const MappingList: React.FC<TFilesList> = (props) => (
  <UploadedFilesList status={MAPPING} {...props} />
);

export const DeletedList: React.FC<TFilesList> = (props) => (
  <UploadedFilesList status={DELETED} {...props} />
);

export const StagedList: React.FC<TFilesList> = (props) => (
  <UploadedFilesList status={STAGED} {...props} />
);

export const OtherList: React.FC<IOtherFilesList> = ({
  files,
  onDeleteFile,
}) => {
  const [isCollapsed, setIsCollapsed] = useState<boolean>(true);
  const [ref, bounds] = useMeasure();

  const mainAnimatedStyle = useSpring({
    from: { height: 0 },
    to: { height: isCollapsed ? 0 : bounds.height },
    config: { duration: 200 },
  });

  return (
    <OtherFilesContainer>
      <OtherFilesHeading>
        <Wrapper>
          <IconWrapper>
            <ApIcon iconColor={colors.grey500} iconName="outline_person" />
          </IconWrapper>
          {`Other user’s files in progress (${files.length} ${
            files.length === 1 ? 'file' : 'files'
          })`}
        </Wrapper>
        <Wrapper>
          {/* <Label>Hide</Label> */}
          <ButtonIcon
            iconName={
              isCollapsed
                ? 'baseline_keyboard_arrow_down'
                : 'baseline_keyboard_arrow_up'
            }
            small
            onClick={() => setIsCollapsed((prevValue) => !prevValue)}
          />
        </Wrapper>
      </OtherFilesHeading>
      <MainWrapper style={mainAnimatedStyle}>
        <OtherFilesList ref={ref}>
          {files.map((f) => (
            <UploadedFilesListItem
              key={f.id}
              {...f}
              showUserName
              onDeleteClick={
                f.canDelete ? () => onDeleteFile?.(f.id.toString()) : undefined
              }
            />
          ))}
        </OtherFilesList>
      </MainWrapper>
    </OtherFilesContainer>
  );
};
